import { useState, useCallback, useMemo, useEffect } from "react";
import { paginationListType } from "@/lib/types";
import { PaginationUtils } from "@/lib/helper";
import { useLocation, useNavigate } from "react-router-dom";

export type UsePaginationProps = {
  data?: Omit<paginationListType<unknown>, "items">;
  initialPage?: number;
  onPageChange?: (page: number) => void;
};

export const usePagination = ({
  data,
  initialPage = 1,
  onPageChange,
}: UsePaginationProps) => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePageChange = useCallback(
    (page: number) => {
      if (!data || !PaginationUtils.isValidPage(page, data.totalPages)) {
        return;
      }

      setCurrentPage(page);
      onPageChange?.(page);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("page", page.toString());
      navigate(`${location.pathname}?${searchParams.toString()}`);
    },
    [data, onPageChange]
  );

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const paginationInfo = useMemo(() => {
    if (!data) {
      return {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        pageSize: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    return {
      currentPage: data.pageIndex,
      totalPages: data.totalPages,
      totalRecords: data.totalRecords,
      pageSize: data.pageSize,
      hasNextPage: data.pageIndex < data.totalPages,
      hasPreviousPage: data.pageIndex > 1,
    };
  }, [data]);

  const goToNextPage = useCallback(() => {
    handlePageChange(paginationInfo.currentPage + 1);
  }, [handlePageChange, paginationInfo.currentPage]);

  const goToPreviousPage = useCallback(() => {
    handlePageChange(paginationInfo.currentPage - 1);
  }, [handlePageChange, paginationInfo.currentPage]);

  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  const goToLastPage = useCallback(() => {
    handlePageChange(paginationInfo.totalPages);
  }, [handlePageChange, paginationInfo.totalPages]);

  return {
    ...paginationInfo,
    handlePageChange,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    currentPage,
  };
};
